import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Users as UsersIcon, Mail, Phone, MapPin, Plus, Edit, Trash2, Save, X, MessageCircle, Eye, User } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

import { getBackendUrl } from '../utils/getBackendUrl';
const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const Team = ({ user }) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('online'); // 'online' | 'recent' | 'name'
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [clientNoteDialogOpen, setClientNoteDialogOpen] = useState(false);
  const [existingClientNotes, setExistingClientNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [noteContent, setNoteContent] = useState('');
  const [clientNoteContent, setClientNoteContent] = useState('');
  const [noteTarget, setNoteTarget] = useState(null); // null = all clients, or specific client object
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    role: 'employee',
    phone: '',
    address: '',
    company: '',
    specialty: '',
    hourly_rate: ''
  });

  useEffect(() => {
    if ((user.role === 'admin' || user.role === 'supervisor')) {
      fetchUsers();
    }
  }, [user.role]);

  // Admin-only access
  if (user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50 pb-20 pt-20 flex items-center justify-center">
        <div className="text-center">
          <UsersIcon className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">Only administrators can access the Team page.</p>
        </div>
      </div>
    );
  }

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      if (Array.isArray(response.data)) {
        setUsers(response.data);
      } else {
        setUsers([]);
      }
    } catch (error) {
      console.error('Failed to fetch users');
      setUsers([]);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/users`, formData);
      toast.success('User added successfully!');
      setIsAddDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add user');
    }
  };

  const handleUpdateUser = async (userId) => {
    try {
      const updateData = {
        name: editingUser.name,
        email: editingUser.email,
        role: editingUser.role,
        phone: editingUser.phone,
        address: editingUser.address,
        company: editingUser.role === 'client' ? editingUser.company : undefined,
        specialty: ['employee', 'sub_contractor'].includes(editingUser.role) ? editingUser.specialty : undefined,
        hourly_rate: ['employee', 'sub_contractor'].includes(editingUser.role) ? editingUser.hourly_rate : undefined
      };
      
      await axios.put(`${API}/admin/users/${userId}`, updateData);
      toast.success('User updated successfully!');
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleResetPassword = async (userId, userName) => {
    const newPassword = prompt(`Enter new password for ${userName}:`);
    if (!newPassword) return;
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('new_password', newPassword);
      
      await axios.put(`${API}/admin/users/${userId}/password`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Password reset successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;
    
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success('User deleted successfully!');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const startEditing = (userToEdit) => {
    setEditingUser({ ...userToEdit });
  };

  const cancelEditing = () => {
    setEditingUser(null);
  };

  const resetForm = () => {
    setFormData({
      email: '',
      name: '',
      password: '',
      role: 'employee',
      phone: '',
      address: '',
      company: '',
      specialty: '',
      hourly_rate: ''
    });
  };

  const handleSendClientNote = async (e) => {
    e.preventDefault();
    if (!noteContent.trim()) return;

    try {
      await axios.post(`${API}/user-notes`, {
        recipient_id: selectedClient.id,
        content: noteContent
      });
      toast.success('Message sent!');
      setNoteDialogOpen(false);
      setNoteContent('');
      setSelectedClient(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    }
  };

  const openNoteDialog = (member) => {
    setSelectedClient(member);
    setNoteDialogOpen(true);
  };

  const openClientNoteDialog = (client) => {
    setNoteTarget(client); // null for all clients, or client object for specific client
    setClientNoteDialogOpen(true);
    fetchExistingClientNotes();
  };

  const fetchExistingClientNotes = async () => {
    setLoadingNotes(true);
    try {
      const res = await axios.get(`${API}/client-notes/admin`);
      setExistingClientNotes(Array.isArray(res.data) ? res.data : []);
    } catch {
      // silent
    } finally {
      setLoadingNotes(false);
    }
  };

  const deleteClientNote = async (noteId) => {
    if (!window.confirm('Delete this note? Clients will no longer see it.')) return;
    try {
      await axios.delete(`${API}/client-notes/${noteId}`);
      setExistingClientNotes((prev) => prev.filter((n) => n.id !== noteId));
      toast.success('Note deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleSendClientNoteToAll = async (e) => {
    e.preventDefault();
    if (!clientNoteContent.trim()) return;

    try {
      await axios.post(`${API}/client-notes`, {
        target_client_id: noteTarget?.id || null, // null means broadcast to all
        content: clientNoteContent
      });
      toast.success(noteTarget ? `Note sent to ${noteTarget.name}!` : 'Note sent to all clients!');
      setClientNoteContent('');
      await fetchExistingClientNotes(); // refresh the list so they can see/manage the new note
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send note');
    }
  };

  const getLastOnlineStatus = (lastSeen, isOnline) => {
    if (!lastSeen) return { text: 'Never', color: 'text-slate-500', dot: 'bg-slate-500' };
    
    if (isOnline) return { text: 'Online now', color: 'text-green-400', dot: 'bg-green-500' };
    
    const now = new Date();
    const lastSeenDate = new Date(lastSeen);
    const diffMs = now - lastSeenDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return { text: 'Just now', color: 'text-green-400', dot: 'bg-green-500' };
    if (diffMins < 5) return { text: `${diffMins}m ago`, color: 'text-green-400', dot: 'bg-green-500' };
    if (diffMins < 60) return { text: `${diffMins}m ago`, color: 'text-yellow-400', dot: 'bg-yellow-500' };
    if (diffHours < 24) return { text: `${diffHours}h ago`, color: 'text-orange-400', dot: 'bg-orange-500' };
    if (diffDays < 7) return { text: `${diffDays}d ago`, color: 'text-slate-400', dot: 'bg-slate-500' };
    return { text: lastSeenDate.toLocaleDateString('en-CA', { timeZone: 'America/Toronto' }), color: 'text-slate-500', dot: 'bg-slate-500' };
  };

  const sortUsersByOnlineStatus = (usersList) => {
    return [...usersList].sort((a, b) => {
      // Online users first
      if (a.is_online && !b.is_online) return -1;
      if (!a.is_online && b.is_online) return 1;
      
      // Then by last_seen (most recent first)
      if (a.last_seen && b.last_seen) {
        return new Date(b.last_seen) - new Date(a.last_seen);
      }
      if (a.last_seen && !b.last_seen) return -1;
      if (!a.last_seen && b.last_seen) return 1;
      
      // Finally by name
      return a.name.localeCompare(b.name);
    });
  };

  const sortUsersByRecentlyAdded = (usersList) => {
    return [...usersList].sort((a, b) => {
      const aDate = a.created_at ? new Date(a.created_at).getTime() : 0;
      const bDate = b.created_at ? new Date(b.created_at).getTime() : 0;
      if (bDate !== aDate) return bDate - aDate; // newest first
      return (a.name || '').localeCompare(b.name || '');
    });
  };

  const sortUsersByName = (usersList) => {
    return [...usersList].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  };

  const applySort = (usersList) => {
    if (sortBy === 'recent') return sortUsersByRecentlyAdded(usersList);
    if (sortBy === 'name') return sortUsersByName(usersList);
    return sortUsersByOnlineStatus(usersList); // default: online
  };

  const canSeeOnline = user?.email === 'admin@dawalconnect.ca';

  const filteredUsers = filter === 'all'
    ? applySort(users)
    : applySort(users.filter(u => u.role === filter));

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-orange-500/20 text-orange-400';
      case 'supervisor': return 'bg-purple-500/20 text-purple-400';
      case 'employee': return 'bg-blue-500/20 text-blue-400';
      case 'sub_contractor': return 'bg-indigo-500/20 text-indigo-400';
      case 'sub-contractor': return 'bg-indigo-500/20 text-indigo-400'; // Backwards compatibility
      case 'client': return 'bg-green-500/20 text-green-400';
      default: return 'bg-slate-500/20 text-slate-400';
    }
  };

  return (
    <div className="page-container" data-testid="team-page">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-white mb-2">Team</h1>
          <p className="text-slate-400">View and manage team members</p>
        </div>
        {(user.role === 'admin' || user.role === 'supervisor') && (
          <div className="flex gap-2">
            <Button 
              onClick={() => openClientNoteDialog(null)}
              className="bg-gradient-to-r from-blue-500 to-blue-600"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Manage Client Notes
            </Button>
            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-green-500 to-green-600">
                <Plus className="w-4 h-4 mr-2" />
                Add User
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white text-gray-900 border-gray-300 max-w-2xl">
              <DialogHeader>
                <DialogTitle className="text-gray-900 text-2xl font-bold">Add New User</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddUser} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email" className="text-gray-900 font-semibold mb-2 block" style={{color: '#111827', fontSize: '14px', fontWeight: '600'}}>Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="name" className="text-gray-900 font-semibold mb-2 block" style={{color: '#111827', fontSize: '14px', fontWeight: '600'}}>Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="password" className="text-gray-900 font-semibold mb-2 block" style={{color: '#111827', fontSize: '14px', fontWeight: '600'}}>Password *</Label>
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="role" className="text-gray-900 font-semibold mb-2 block" style={{color: '#111827', fontSize: '14px', fontWeight: '600'}}>Role *</Label>
                    <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="supervisor">Supervisor</SelectItem>
                        <SelectItem value="employee">Employee</SelectItem>
                        <SelectItem value="sub_contractor">Sub-contractor</SelectItem>
                        <SelectItem value="client">Client</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-gray-900 font-semibold mb-2 block" style={{color: '#111827', fontSize: '14px', fontWeight: '600'}}>Phone</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="address" className="text-gray-900 font-semibold mb-2 block" style={{color: '#111827', fontSize: '14px', fontWeight: '600'}}>Address</Label>
                    <Input
                      id="address"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    />
                  </div>
                  {formData.role === 'client' && (
                    <div>
                      <Label htmlFor="company" className="text-gray-900 font-semibold mb-2 block" style={{color: '#111827', fontSize: '14px', fontWeight: '600'}}>Company</Label>
                      <Input
                        id="company"
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      />
                    </div>
                  )}
                  {['employee', 'sub_contractor'].includes(formData.role) && (
                    <>
                      <div>
                        <Label htmlFor="specialty" className="text-gray-900 font-semibold mb-2 block" style={{color: '#111827', fontSize: '14px', fontWeight: '600'}}>Specialty</Label>
                        <Input
                          id="specialty"
                          value={formData.specialty}
                          onChange={(e) => setFormData({ ...formData, specialty: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="hourly_rate" className="text-gray-900 font-semibold mb-2 block" style={{color: '#111827', fontSize: '14px', fontWeight: '600'}}>Hourly Rate</Label>
                        <Input
                          id="hourly_rate"
                          type="number"
                          step="0.01"
                          value={formData.hourly_rate}
                          onChange={(e) => setFormData({ ...formData, hourly_rate: e.target.value })}
                        />
                      </div>
                    </>
                  )}
                </div>
                <Button type="submit" className="w-full bg-gradient-to-r from-green-500 to-green-600">
                  Add User
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      <div className="glass-card p-6 mb-6">
        <div className="flex gap-3 flex-wrap">
          <button
            onClick={() => setFilter('all')}
            data-testid="filter-all"
            className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
              filter === 'all'
                ? 'bg-orange-500 text-white'
                : 'bg-slate-800/50 text-slate-300 hover:bg-slate-800'
            }`}
          >
            All ({users.length})
          </button>
          <button
            onClick={() => setFilter('admin')}
            data-testid="filter-admin"
            className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
              filter === 'admin'
                ? 'bg-orange-500 text-white'
                : 'bg-slate-800/50 text-slate-300 hover:bg-slate-800'
            }`}
          >
            Admins ({users.filter(u => u.role === 'admin').length})
          </button>
          <button
            onClick={() => setFilter('supervisor')}
            data-testid="filter-supervisor"
            className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
              filter === 'supervisor'
                ? 'bg-orange-500 text-white'
                : 'bg-slate-800/50 text-slate-300 hover:bg-slate-800'
            }`}
          >
            Supervisors ({users.filter(u => u.role === 'supervisor').length})
          </button>
          <button
            onClick={() => setFilter('employee')}
            data-testid="filter-employee"
            className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
              filter === 'employee'
                ? 'bg-orange-500 text-white'
                : 'bg-slate-800/50 text-slate-300 hover:bg-slate-800'
            }`}
          >
            Employees ({users.filter(u => u.role === 'employee').length})
          </button>
          <button
            onClick={() => setFilter('sub_contractor')}
            data-testid="filter-subcontractor"
            className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
              filter === 'sub_contractor'
                ? 'bg-orange-500 text-white'
                : 'bg-slate-800/50 text-slate-300 hover:bg-slate-800'
            }`}
          >
            Sub Contractors ({users.filter(u => u.role === 'sub_contractor').length})
          </button>
          <button
            onClick={() => setFilter('client')}
            data-testid="filter-client"
            className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
              filter === 'client'
                ? 'bg-orange-500 text-white'
                : 'bg-slate-800/50 text-slate-300 hover:bg-slate-800'
            }`}
          >
            Clients ({users.filter(u => u.role === 'client').length})
          </button>
        </div>
      </div>

      {/* Sort selector */}
      <div className="mb-4 flex items-center gap-3 text-sm">
        <label htmlFor="team-sort" className="text-slate-600 font-semibold flex items-center gap-2">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
          </svg>
          Sort by:
        </label>
        <select
          id="team-sort"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="bg-white border border-slate-300 rounded-md px-3 py-1.5 text-sm font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-400"
          data-testid="team-sort-select"
        >
          {canSeeOnline && <option value="online">Online status (most recent)</option>}
          <option value="recent">Recently added (newest first)</option>
          <option value="name">Name (A → Z)</option>
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredUsers.map((member) => (
          <div key={member.id} className="glass-card p-6 slide-up" data-testid={`user-card-${member.id}`}>
            {editingUser && editingUser.id === member.id ? (
              // Edit Mode
              <div className="space-y-4">
                <div>
                  <Label>Name</Label>
                  <Input
                    value={editingUser.name}
                    onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={editingUser.email}
                    onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Role / Position</Label>
                  <Select 
                    value={editingUser.role} 
                    onValueChange={(value) => setEditingUser({ ...editingUser, role: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="supervisor">Supervisor</SelectItem>
                      <SelectItem value="employee">Employee</SelectItem>
                      <SelectItem value="sub_contractor">Sub-Contractor</SelectItem>
                      <SelectItem value="client">Client</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={editingUser.phone || ''}
                    onChange={(e) => setEditingUser({ ...editingUser, phone: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Address</Label>
                  <Input
                    value={editingUser.address || ''}
                    onChange={(e) => setEditingUser({ ...editingUser, address: e.target.value })}
                  />
                </div>
                {editingUser.role === 'client' && (
                  <div>
                    <Label>Company</Label>
                    <Input
                      value={editingUser.company || ''}
                      onChange={(e) => setEditingUser({ ...editingUser, company: e.target.value })}
                    />
                  </div>
                )}
                {['employee', 'sub_contractor'].includes(editingUser.role) && (
                  <>
                    <div>
                      <Label>Specialty</Label>
                      <Input
                        value={editingUser.specialty || ''}
                        onChange={(e) => setEditingUser({ ...editingUser, specialty: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Hourly Rate</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={editingUser.hourly_rate || ''}
                        onChange={(e) => setEditingUser({ ...editingUser, hourly_rate: e.target.value })}
                      />
                    </div>
                  </>
                )}
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleUpdateUser(member.id)}
                      className="flex-1 bg-gradient-to-r from-green-500 to-green-600"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Save
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={cancelEditing}
                      className="flex-1"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleResetPassword(member.id, member.name)}
                    className="w-full bg-yellow-600 hover:bg-yellow-700"
                  >
                    Reset Password
                  </Button>
                </div>
              </div>
            ) : (
              // View Mode
              <>
                {/* User Name - Prominent Display at Top */}
                <div className="mb-4">
                  <div className="inline-block px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-700 rounded-lg shadow-md">
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-bold text-white uppercase tracking-wider">
                        {member.name}
                      </h3>
                      {canSeeOnline && member.is_online && (
                        <span className="text-xs text-green-300 font-semibold">● Online</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-4 mb-4">
                  <div className="relative">
                    <Avatar className="w-16 h-16">
                      <AvatarImage src={member.profile_picture ? `${BACKEND_URL}${member.profile_picture}` : undefined} />
                      <AvatarFallback className="bg-orange-500 text-white text-xl">
                        {member.name.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    {canSeeOnline && member.is_online && (
                      <div className="absolute bottom-0 right-0 w-4 h-4 bg-green-500 border-2 border-white rounded-full"></div>
                    )}
                  </div>
                  <div className="flex-1">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getRoleBadgeColor(member.role)}`}>
                      {member.role.replace('_', ' ').toUpperCase()}
                    </span>
                    {canSeeOnline && member.current_page && member.is_online && (
                      <p className="text-xs text-slate-400 mt-1">Viewing: {member.current_page}</p>
                    )}
                    {/* Last Online Status - Walter only */}
                    {canSeeOnline && (() => {
                      const status = getLastOnlineStatus(member.last_seen, member.is_online);
                      return (
                        <div className="flex items-center gap-2 mt-2">
                          <div className={`w-2 h-2 rounded-full ${status.dot}`}></div>
                          <span className={`text-xs ${status.color} font-medium`}>
                            {status.text}
                          </span>
                        </div>
                      );
                    })()}
                    {canSeeOnline && !member.is_online && member.last_seen && (
                      <p className="text-xs text-slate-400 mt-1">
                        Last seen: {new Date(member.last_seen).toLocaleString('en-CA', { timeZone: 'America/Toronto' })}
                      </p>
                    )}
                    {user?.role === 'admin' && member.role !== 'admin' && (
                      <p className="text-xs text-cyan-400 font-medium mt-1" data-testid={`visit-count-${member.id}`}>
                        Online {member.visit_count || 0} time{(member.visit_count || 0) !== 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                </div>
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-300">{member.email}</span>
                  </div>
                  {member.phone && (
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-300">{member.phone}</span>
                    </div>
                  )}
                  {member.address && (
                    <div className="flex items-center gap-2 text-sm">
                      <MapPin className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-300 line-clamp-1">{member.address}</span>
                    </div>
                  )}
                  {member.specialty && (
                    <div className="text-sm">
                      <span className="text-slate-400">Specialty: </span>
                      <span className="text-slate-300">{member.specialty}</span>
                    </div>
                  )}
                  {member.hourly_rate && (
                    <div className="text-sm">
                      <span className="text-slate-400">Rate: </span>
                      <span className="text-slate-300">${member.hourly_rate}/hr</span>
                    </div>
                  )}
                  {member.company && (
                    <div className="text-sm">
                      <span className="text-slate-400">Company: </span>
                      <span className="text-slate-300">{member.company}</span>
                    </div>
                  )}
                </div>
                {(user.role === 'admin' || user.role === 'supervisor') && member.id !== user.id && (
                  <div className="flex flex-col gap-2">
                    {member.role === 'client' && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => navigate(`/client-dashboard/${member.id}`)}
                          className="bg-gradient-to-r from-indigo-500 to-indigo-600"
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          View Dashboard
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => navigate(`/profile/${member.id}`)}
                          className="bg-gradient-to-r from-purple-500 to-purple-600"
                        >
                          <User className="w-4 h-4 mr-2" />
                          View Profile
                        </Button>
                      </>
                    )}
                    {member.role === 'sub_contractor' && (
                      <Button
                        size="sm"
                        onClick={() => navigate(`/sub-dashboard/${member.id}`)}
                        className="bg-gradient-to-r from-indigo-500 to-indigo-600"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        View Dashboard
                      </Button>
                    )}
                    {member.role === 'client' ? (
                      <Button
                        size="sm"
                        onClick={() => openClientNoteDialog(member)}
                        className="bg-gradient-to-r from-blue-500 to-blue-600"
                      >
                        <MessageCircle className="w-4 h-4 mr-2" />
                        Send Note
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => openNoteDialog(member)}
                        className="bg-gradient-to-r from-blue-500 to-blue-600"
                      >
                        <MessageCircle className="w-4 h-4 mr-2" />
                        Send Message
                      </Button>
                    )}
                    <Button
                      size="sm"
                      onClick={() => handleResetPassword(member.id, member.name)}
                      className="w-full bg-yellow-600 hover:bg-yellow-700"
                    >
                      Reset Password
                    </Button>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => startEditing(member)}
                        className="flex-1"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteUser(member.id)}
                        className="flex-1 border-red-600 text-red-400 hover:bg-red-600 hover:text-white"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        ))}
      </div>

      {filteredUsers.length === 0 && (
        <div className="text-center py-16">
          <UsersIcon className="w-16 h-16 text-slate-500 mx-auto mb-4" />
          <p className="text-slate-400 text-lg">No team members found</p>
        </div>
      )}

      {/* Send Message to Client Dialog */}
      <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
        <DialogContent className="bg-slate-800 text-white border-slate-700">
          <DialogHeader>
            <DialogTitle>Send Message to {selectedClient?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSendClientNote} className="space-y-4">
            <div>
              <Label>Message</Label>
              <Textarea
                value={noteContent}
                onChange={(e) => setNoteContent(e.target.value)}
                placeholder="Type your message to the client..."
                className="min-h-[150px] mt-1"
                required
              />
            </div>
            <Button type="submit" className="w-full bg-gradient-to-r from-blue-500 to-blue-600">
              Send Message
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Client Note Dialog (for all clients or specific client) */}
      <Dialog open={clientNoteDialogOpen} onOpenChange={(o) => { setClientNoteDialogOpen(o); if (!o) { setNoteTarget(null); setClientNoteContent(''); } }}>
        <DialogContent className="bg-slate-800 text-white border-slate-700 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {noteTarget ? `Notes for ${noteTarget.name}` : 'Notes to All Clients'}
            </DialogTitle>
          </DialogHeader>

          {/* Existing notes — view and delete */}
          <div className="space-y-2">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Active notes</p>
            {loadingNotes ? (
              <p className="text-sm text-slate-400 py-3 text-center">Loading…</p>
            ) : existingClientNotes.length === 0 ? (
              <p className="text-sm text-slate-400 py-3 text-center italic">No notes yet — send one below.</p>
            ) : (
              <div className="space-y-2 max-h-56 overflow-y-auto pr-1" data-testid="existing-client-notes">
                {existingClientNotes.map((n) => (
                  <div key={n.id} className="bg-slate-900/60 border border-slate-700 rounded-lg p-3 flex items-start gap-2" data-testid={`existing-note-${n.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${n.is_broadcast ? 'bg-blue-600 text-white' : 'bg-emerald-600 text-white'}`}>
                          {n.is_broadcast ? '📢 All Clients' : '📝 Personal'}
                        </span>
                        {!n.is_broadcast && n.target_client_id && (
                          <span className="text-[10px] text-slate-300 truncate">
                            → {users.find((c) => c.id === n.target_client_id)?.name || n.target_client_id}
                          </span>
                        )}
                        <span className="text-[10px] text-slate-500 ml-auto">{n.created_at ? new Date(n.created_at).toLocaleDateString('en-CA', { timeZone: 'America/Toronto', month: 'short', day: 'numeric' }) : ''}</span>
                      </div>
                      <p className="text-sm text-slate-200 whitespace-pre-wrap break-words line-clamp-4">{n.content}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => deleteClientNote(n.id)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded p-1.5 flex-shrink-0"
                      title="Delete this note"
                      data-testid={`delete-note-${n.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="border-t border-slate-700 my-3" />

          <form onSubmit={handleSendClientNoteToAll} className="space-y-4">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Send a new note</p>
            <div className="bg-blue-900/30 border border-blue-700 rounded-lg p-3">
              <p className="text-sm text-blue-200">
                {noteTarget 
                  ? `This note will be visible to ${noteTarget.name} on their client dashboard.`
                  : '📢 This note will be visible to ALL clients on their dashboards.'
                }
              </p>
            </div>
            <div>
              <Label>Note Content</Label>
              <Textarea
                value={clientNoteContent}
                onChange={(e) => setClientNoteContent(e.target.value)}
                placeholder="Type your note here..."
                className="min-h-[120px] mt-1"
                required
              />
              <p className="text-xs text-slate-400 mt-1">
                Clients will see this note on their dashboard under "Notes from Admin"
              </p>
            </div>
            <Button type="submit" className="w-full bg-gradient-to-r from-blue-500 to-blue-600">
              {noteTarget ? 'Send Note to Client' : 'Send Note to All Clients'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default Team;